import { Card, Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useCallback, useMemo } from 'react';

import { BODY_LINE_HEIGHT, MODAL_CARD_WIDTH } from '@jane/business-admin/util';
/* eslint-disable-next-line no-restricted-imports */
import type { ProductThresholdConditions } from '@jane/shared/models';

import { PriceConditions } from '../shared/specials/modal/conditions/PriceConditions';
import { BrandConditions } from '../shared/specials/modal/conditions/brandConditions/BrandConditions';
import { CategoryConditions } from '../shared/specials/modal/conditions/categoryConditions/CategoryConditions';
import { convertRulesToString } from '../shared/specials/modal/conditions/convertRulesToString';
import { LoadingCondition } from '../shared/specials/modal/conditions/generic_components/LoadingCondition';
import {
  ConditionType,
  FORM_FIELD_DEFAULT_MARGIN,
} from '../shared/specials/modal/form';

interface Props {
  isLoading: boolean;
}

// Empty prefix since MenuRowConditionsCard uses top-level form fields (rules, apply_to)
const MENU_ROW_FIELD_PREFIX = '';

export const applyToOptions = [
  {
    id: 'advanced',
    label: 'Category, brand, and price',
    value: 'advanced',
  },
  {
    id: 'products',
    label: 'Individual products',
    value: 'products',
  },
];

export const MenuRowConditionsCard = ({ isLoading }: Props) => {
  const { setValue, watch, getValues } = useFormContext();

  const rulesFieldPath = 'rules';
  const applyToFieldPath = 'apply_to';

  const rulesWatch = watch(rulesFieldPath);
  const applyToWatch = watch(applyToFieldPath);

  const prices = useMemo(() => {
    return get(rulesWatch, 'product_threshold', {});
  }, [convertRulesToString(rulesWatch)]);

  const onPricesUpdate = useCallback(
    (prices: ProductThresholdConditions) => {
      setValue(`${rulesFieldPath}.product_threshold`, prices, {
        shouldDirty: true,
      });
    },
    [rulesFieldPath, setValue]
  );

  const onCategoriesUpdate = useCallback(
    (kinds: any, includesOrExcludes: 'includes' | 'excludes') => {
      const currentRules = getValues(rulesFieldPath);
      const rules =
        currentRules && currentRules[includesOrExcludes]
          ? currentRules[includesOrExcludes]
          : null;

      const firstCondition = rules?.[0] ?? {};
      const newRules = {
        ...currentRules,
        [includesOrExcludes]: [{ ...firstCondition, kinds }],
      };

      setValue(rulesFieldPath, newRules, { shouldDirty: true });
    },
    [rulesFieldPath, getValues, setValue]
  );

  return (
    <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
      <Card.Content>
        <Flex p={24} flexDirection="column">
          <Typography variant="header-bold" mb={FORM_FIELD_DEFAULT_MARGIN}>
            Conditions
          </Typography>
          <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
            <Typography variant="body-bold" mb={FORM_FIELD_DEFAULT_MARGIN}>
              Apply to
            </Typography>
            {isLoading ? (
              <Skeleton direction="row" animate>
                <Skeleton.Bone height={BODY_LINE_HEIGHT} width="40%" />
                <Skeleton.Bone height={BODY_LINE_HEIGHT} ml={12} width="40%" />
              </Skeleton>
            ) : (
              <Form.RadioFieldGroup
                shouldUnregister={false}
                name={applyToFieldPath}
                row={true}
                options={applyToOptions}
              />
            )}
          </Flex>
          {isLoading ? (
            <>
              <LoadingCondition />
              <LoadingCondition />
              <LoadingCondition />
            </>
          ) : (
            <>
              {applyToWatch === 'advanced' && (
                <>
                  <CategoryConditions
                    fieldPrefix={MENU_ROW_FIELD_PREFIX}
                    isGlobalSpecial={false}
                    type={ConditionType.Categories}
                    onConditionsUpdate={onCategoriesUpdate}
                  />
                  <BrandConditions
                    fieldPrefix={MENU_ROW_FIELD_PREFIX}
                    isGlobalSpecial={false}
                    type={ConditionType.Brands}
                  />
                  <PriceConditions
                    fieldPrefix={MENU_ROW_FIELD_PREFIX}
                    conditions={prices}
                    onConditionsUpdate={onPricesUpdate}
                    type={ConditionType.Price}
                  />
                </>
              )}
            </>
          )}
        </Flex>
      </Card.Content>
    </Card>
  );
};
